/*
    The 2D placement diagram for lacrosse, as an inline SVG data URL.

    The tennis court diagram cannot be stretched to serve. Its frame is
    18.3 m wide (+/-9.15) and the lacrosse spots reach +/-15.9 - width is the
    binding constraint, not length. So lacrosse gets its own frame: a 40 x 40 m
    square centred on the goal, at 12.5 px/m.

    ONE STATIC DIAGRAM SERVES ALL THREE VARIANTS. That is not a simplification.
    At this zoom no end line, sideline, restraining line, wing line or centre
    circle is in frame for any variant, and everything that IS in frame - the
    goal, the crease and goal circle, the 8 m arc, the 12 m fan with its flat
    sides and 45 degree legs, the arc's centre hash and the below-goal dots -
    is identical in women's and mixed and absent from men's. Drawing the union
    is correct for two variants and harmless for the third, whose arc and fan
    the 3D view shows as dashed drill rings.

    THE TWO-CAGE DIAGRAM CARRIES NO PAINT. That drill is set up on a practice
    field rather than a marked one, so its diagram is two cages and the line
    between them and nothing else - see buildSvg.

    A `data:image/svg+xml` URL is a string and a valid `<img src>`, so
    CourtImages stays `Record<CourtSize, Record<Sport, string>>` and all four
    of its consumers are untouched.

    Rejected alternatives: a canvas plus toDataURL() (needs a DOM at module
    scope, so it breaks in any DOM-less context and produces a much larger
    base64 PNG), and widening CourtImages to `(variant) => string` (ripples to
    four consumers for a variant-dependence that does not exist).
*/
import {
    arcHalfAngle,
    DRILL_GEOMETRY,
    GOAL,
    LACROSSE_FIELDS,
    TWO_GOAL_OFFSET_X,
    YD,
} from "./geometry";
import { LACROSSE_TWO_CAGE_YARDS } from "./spots";

/**
 * How much field the diagram shows, each way from the goal centre.
 *
 * 20 m covers every spot - the widest is 15.9 m and the deepest 17.2 m - with
 * enough margin that a marker at the edge is not clipped.
 */
export const DIAGRAM_HALF_EXTENT_M = 20;

/** Diagram size in pixels. Square, which matters - see below. */
export const DIAGRAM_SIZE_PX = 500;

/**
 * SQUARE PIXELS ARE LOAD-BEARING.
 *
 * projected2Image() scales x and y by independent widthPPM and heightPPM read
 * off the CourtTable entry, so a frame whose metres-per-pixel differ by axis
 * silently skews every marker off the markings it is supposed to sit on. A
 * square frame over a square extent makes the two scales equal by
 * construction.
 */
export const DIAGRAM_PX_PER_M = DIAGRAM_SIZE_PX / (DIAGRAM_HALF_EXTENT_M * 2);

/**
 * Painted lines are 5 cm, which is 0.6 px here and invisible. The diagram is a
 * plan to place a machine against, not a scale drawing, so lines are drawn at
 * a legible width instead of a true one.
 */
const STROKE = 1.4;
const THIN_STROKE = 1;

/** Physics metres -> diagram pixels. +y is up the page, so py is inverted. */
function px(x: number): number {
    return round(x * DIAGRAM_PX_PER_M + DIAGRAM_SIZE_PX / 2);
}
function py(y: number): number {
    return round(DIAGRAM_SIZE_PX / 2 - y * DIAGRAM_PX_PER_M);
}
function len(metres: number): number {
    return round(metres * DIAGRAM_PX_PER_M);
}
function round(value: number): number {
    return Math.round(value * 100) / 100;
}

/*
    Named colours only. A raw "#" inside a data: URL terminates it as a
    fragment identifier, so every colour here is a CSS named colour and
    diagram.test.ts asserts the URL carries no bare "#".

    Exported so the tests can name a role rather than a literal - they use the
    colours as sentinels for "is this marking drawn at all", which was silently
    coupled to the palette until the ground stopped being white.
*/
export const DIAGRAM_COLOR = {
    /*
        Turf, not paper.

        `forestgreen` is the closest CSS named colour to the 3D field's
        surface (#47903b), which the named-colour rule above rules out
        directly. A white ground was almost unreadable in the picker: the
        selected player marker is a light pin, and a light pin on white is the
        one combination that hides it.
    */
    ground: "forestgreen",
    /*
        Field paint is white, and on turf it has to be - `dimgray` was chosen
        against a white ground and inverts to near-invisible on green.
    */
    line: "white",
    faint: "silver",
    cage: "darkorange",
    /*
        The two-cage range guide. Deliberately labelled in yards as well as
        drawn: a coach reading this needs to know the line runs 3 to 15, and a
        line that only differs from the paint by colour tells them nothing.

        `gold` rather than the old `teal`, which was a dark blue-green on a
        white page and is nearly the ground colour on a green one.
    */
    guide: "gold",
};

const COLOR = DIAGRAM_COLOR;

/** Straight `x1,y1 -> x2,y2` in metres. */
function line(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    stroke = STROKE,
    color = COLOR.line,
): string {
    return (
        `<line x1="${px(x1)}" y1="${py(y1)}" x2="${px(x2)}" y2="${py(y2)}"` +
        ` stroke="${color}" stroke-width="${stroke}"/>`
    );
}

/** Circle about the goal centre, radius in metres. */
function circle(r: number, stroke = STROKE, color = COLOR.line): string {
    return (
        `<circle cx="${px(0)}" cy="${py(0)}" r="${len(r)}"` +
        ` fill="none" stroke="${color}" stroke-width="${stroke}"/>`
    );
}

/**
 * Arc about the goal centre, opening DOWNFIELD (-y), spanning +/-halfAngle off
 * the downfield axis.
 *
 * Bearing b maps to physics (r sin b, -r cos b), which is the same convention
 * spots.ts uses, so an arc drawn here and a spot placed on it cannot disagree.
 */
function arc(r: number, halfAngle: number, stroke = STROKE): string {
    const from = { x: r * Math.sin(-halfAngle), y: -r * Math.cos(-halfAngle) };
    const to = { x: r * Math.sin(halfAngle), y: -r * Math.cos(halfAngle) };
    // sweep-flag 0: from the -x end to the +x end the short way, through
    // straight-downfield. large-arc-flag is 0 while halfAngle < 90 deg.
    const large = halfAngle > Math.PI / 2 ? 1 : 0;
    return (
        `<path d="M ${px(from.x)} ${py(from.y)}` +
        ` A ${len(r)} ${len(r)} 0 ${large} 0 ${px(to.x)} ${py(to.y)}"` +
        ` fill="none" stroke="${COLOR.line}" stroke-width="${stroke}"/>`
    );
}

/** A short text label, anchored at a point in metres. */
function text(
    x: number,
    y: number,
    body: string,
    anchor: "start" | "middle" | "end" = "start",
): string {
    return (
        `<text x="${px(x)}" y="${py(y)}" fill="${COLOR.guide}"` +
        ` font-family="sans-serif" font-size="13" font-weight="600"` +
        ` text-anchor="${anchor}">${body}</text>`
    );
}

/**
 * One cage, mouth on the goal line at `cx`, netting tapering behind it.
 *
 * Same orange as the 3D pipe, so the two views read as the same object, and the
 * netting tapers behind the mouth (+y) - which is what tells a reader which way
 * the machine is meant to face.
 */
function cage(cx: number): string[] {
    const gw = GOAL.halfWidth;
    return [
        `<path d="M ${px(cx - gw)} ${py(0)} L ${px(cx)} ${py(GOAL.netDepth)}` +
            ` L ${px(cx + gw)} ${py(0)} Z"` +
            ` fill="${COLOR.faint}" fill-opacity="0.35" stroke="${COLOR.faint}" stroke-width="${THIN_STROKE}"/>`,
        line(cx - gw, 0, cx + gw, 0, 3, COLOR.cage),
    ];
}

/**
 * The two-cage drill's range guide: the bisector line the machine stands on,
 * ticked at every whole yard and labelled at both ends.
 *
 * Drawn because the two-cage picker offers no clickable markers - the distance
 * is a slider - so without this the diagram would give no hint of where the
 * machine may go or how far the range reaches.
 */
function rangeGuide(): string[] {
    const { near, far } = LACROSSE_TWO_CAGE_YARDS;
    const out = [
        `<line x1="${px(0)}" y1="${py(-near * YD)}"` +
            ` x2="${px(0)}" y2="${py(-far * YD)}"` +
            ` stroke="${COLOR.guide}" stroke-width="${STROKE}" stroke-dasharray="6 4"/>`,
    ];
    for (let yards = near; yards <= far; yards += 1) {
        const y = -yards * YD;
        const half = yards % 5 === 0 ? 0.6 : 0.3;
        out.push(line(-half, y, half, y, THIN_STROKE, COLOR.guide));
    }
    out.push(text(0.9, -near * YD - 0.35, `${near} yd`));
    out.push(text(0.9, -far * YD - 0.35, `${far} yd`));
    return out;
}

/**
 * Every painted marking in frame: the 12 m fan, the 8 m arc and its 45 degree
 * legs, both goal-area circles, the arc's centre hash and the below-goal dots.
 *
 * Drawn for the ONE-cage placement only. Every spot in that mode is a radius
 * off one of these, so the paint is what a coach lines the machine up against.
 */
function markings(): string[] {
    const { goalCircleR, arcR, fanR, angleLeg, dotBack } = DRILL_GEOMETRY;
    const creaseR = LACROSSE_FIELDS.MENS.creaseR!;
    const half = arcHalfAngle(LACROSSE_FIELDS.WOMENS)!;
    const leg = angleLeg / Math.SQRT2;

    // 12 m fan: the semicircle, its flat sides along the GLE (goal line
    // extended), and the 45 degree legs closing the 8 m arc onto the goal
    // circle.
    const out = [arc(fanR, Math.PI / 2), arc(arcR, half)];
    for (const sx of [1, -1]) {
        out.push(line(sx * goalCircleR, 0, sx * fanR, 0));
        out.push(line(sx * goalCircleR, 0, sx * (goalCircleR + leg), -leg));
    }

    /*
        Both goal-area circles. The men's crease is 9 ft (2.743 m) and the
        women's goal circle 8'6" (2.6 m) - 14 cm apart, so they read as a
        double line at this scale, and each is correct on some field.
    */
    out.push(circle(creaseR, THIN_STROKE));
    out.push(circle(goalCircleR, THIN_STROKE));

    // The arc's centre hash, 1 ft on the goal-line perpendicular. The most
    // findable mark on the field.
    out.push(line(-0.152, -arcR, 0.152, -arcR, STROKE));

    // Below-goal dots, 5 yd behind the goal line at +/-goalCircleR.
    for (const sx of [1, -1]) {
        out.push(
            line(
                sx * goalCircleR - 0.15,
                dotBack,
                sx * goalCircleR + 0.15,
                dotBack,
                STROKE,
            ),
        );
    }
    return out;
}

function buildSvg(twoCage: boolean): string {
    const parts: string[] = [
        `<rect x="0" y="0" width="${DIAGRAM_SIZE_PX}" height="${DIAGRAM_SIZE_PX}" fill="${COLOR.ground}"/>`,
    ];

    /*
        NO PAINT IN TWO-CAGE MODE. A two-cage drill gets set up on a practice
        field, which has no markings on it - so drawing the arc and the fan
        would be a picture of a field the coach is not standing on, and worse,
        it would suggest they should be lining the cages up against paint that
        is not there. All they need is the two cages and the line between them.

        One cage is the other way round: every spot in that mode is a radius off
        one of these markings, so the paint IS the thing being placed against.
    */
    if (twoCage) {
        const half = TWO_GOAL_OFFSET_X / 2;
        // Cages last so they sit on top of the guide line running between them.
        parts.push(...rangeGuide(), ...cage(-half), ...cage(half));
    } else {
        parts.push(...markings(), ...cage(0));
    }

    /*
        The opening tag is ONE template literal, deliberately. Split across
        concatenated literals - which is how this was written - the production
        minifier folded the join and dropped a quote, emitting

            width="500" height="500 viewBox="0 0 500 500"

        The trigger is a literal that ends in a substitution immediately
        followed by a closing quote (`height="${X}"`) being concatenated with
        the next one; the other builders here end in `/>` and came through
        intact. That is invalid XML, so the browser refused the data: URL, the
        court image reported a load error, and the position editors showed
        "Unable to load required images" - with no failed request in the
        network tab, because a data: URL never makes one.

        It only ever reproduced in an optimised build, so keep this as a single
        literal and check `npm run build` output, not just the dev server, if
        it is ever reflowed.
    */
    const open = `<svg xmlns="http://www.w3.org/2000/svg" width="${DIAGRAM_SIZE_PX}" height="${DIAGRAM_SIZE_PX}" viewBox="0 0 ${DIAGRAM_SIZE_PX} ${DIAGRAM_SIZE_PX}">`;

    return open + parts.join("") + `</svg>`;
}

/**
 * The diagram, as an `<img src>`-ready data URL.
 *
 * Explicit `width` and `height` as well as `viewBox`: a viewBox-only SVG in a
 * data: URL has no reliable intrinsic size, and TrainerCourtPosition sizes its
 * canvas off the image's natural dimensions.
 */
export const LACROSSE_DIAGRAM = `data:image/svg+xml,${encodeURIComponent(buildSvg(false))}`;

/**
 * The same frame with the two-cage drill set up: a pair of cages straddling the
 * goal's centreline 8 ft apart, and the bisector line the machine stands on
 * ticked out to 15 yd.
 *
 * No painted markings at all - these drills run on a practice field, which has
 * none. See buildSvg.
 *
 * A second string rather than a parameter, so it is still a constant a
 * `Record` can hold and the diagram is built once at module load.
 */
export const LACROSSE_TWO_CAGE_DIAGRAM = `data:image/svg+xml,${encodeURIComponent(buildSvg(true))}`;

/**
 * Pixel geometry for CourtTable, matching the SVG above.
 *
 * `yOffset === height / 2` is not a style choice, it is forced. Two places map
 * physics y to image y and they are written differently:
 *
 *     Position/util.ts projected2Image   y = yOffset - y * heightPPM
 *     UserAssisted/CourtOverview.tsx     y = height - yOffset - y * heightPPM
 *
 * Those agree if and only if `yOffset === height / 2`. Every other sport's
 * entry has an offset that does not satisfy it, which is a latent
 * inconsistency between the two views; putting the goal line exactly halfway
 * down a square frame makes lacrosse the one sport where both formulas give
 * the same answer. diagram.test.ts asserts it, because nothing else would.
 *
 * FULL and HALF are identical: lacrosse has no meaningful half-field, and the
 * spots are all inside one 40 m square either way.
 */
export const LACROSSE_COURT_DIMENSIONS = {
    widthMeters: DIAGRAM_HALF_EXTENT_M * 2,
    heightMeters: {
        FULL: DIAGRAM_HALF_EXTENT_M * 2,
        HALF: DIAGRAM_HALF_EXTENT_M * 2,
    },
    width: DIAGRAM_SIZE_PX,
    height: {
        FULL: DIAGRAM_SIZE_PX,
        HALF: DIAGRAM_SIZE_PX,
    },
    xOffset: DIAGRAM_SIZE_PX / 2,
    yOffset: {
        FULL: DIAGRAM_SIZE_PX / 2,
        HALF: DIAGRAM_SIZE_PX / 2,
    },
};
